import { abweisen, berlinDatum, ereignis, verlangen, type Ereignis, type GespeichertesEreignis } from "../gemeinsam/index.js";
import { jagdzustandZu, naechsterWechsel, type Eingriff, type Eingriffsart, type Jagdabschnitt, type Jagdzustand } from "./jagdzustand.js";

/**
 * Aggregat Jagdruhe (Domänenmodell §5, ADR-0017): Jagdzeitplan, Eingriffe und der zuletzt festgehaltene Jagdzustand.
 * Ein Strom je Drückjagd (`Jagdruhe:<DrückjagdId>`, ereignisspeicher.md §3). Befehle sind reine Funktionen
 * `(zustand, angaben, jetzt) → Ereignis[]`; Berechtigung prüft die Anwendung vorher (kein-zugriff, nicht-veranstalter,
 * rolle-fehlt), Status der Drückjagd und Fachregeln prüft diese Datei.
 */

export const JAGDRUHE_EREIGNIS = {
  JagdzeitplanFestgelegt: "JagdzeitplanFestgelegt",
  JagdzeitplanGeaendert: "JagdzeitplanGeaendert",
  HahnInRuhAusgerufen: "HahnInRuhAusgerufen",
  JagdFreigegeben: "JagdFreigegeben",
  ZumZeitplanZurueckgekehrt: "ZumZeitplanZurueckgekehrt",
  JagdzustandGewechselt: "JagdzustandGewechselt",
} as const;

export type Ausloeser = "Jagdzeitplan" | "Jagdzeitplan ändern" | "Hahn in Ruh ausrufen" | "Jagd freigeben" | "Zum Zeitplan zurückkehren";

export interface Eingriffseintrag extends Eingriff {
  /** TeilnehmerId der handelnden Person */
  person: string;
  grund: string | null;
}

export interface JagdruheZustand {
  festgelegt: boolean;
  plan: Jagdabschnitt[];
  eingriff: Eingriffseintrag | null;
  /** zuletzt mit `JagdzustandGewechselt` festgehaltener Zustand; ohne Wechsel ruht die Jagd (F-060) */
  gemeldet: Jagdzustand;
}

export const KEINE_JAGDRUHE: JagdruheZustand = { festgelegt: false, plan: [], eingriff: null, gemeldet: "Jagd ruht" };

function datum(w: unknown): Date {
  return w instanceof Date ? w : new Date(String(w));
}

function abschnitteAus(w: unknown): Jagdabschnitt[] {
  return ((w as Array<{ Beginn: unknown; Ende: unknown }> | undefined) ?? []).map((a) => ({ Beginn: datum(a.Beginn), Ende: datum(a.Ende) }));
}

const ART: Record<string, Eingriffsart> = {
  [JAGDRUHE_EREIGNIS.HahnInRuhAusgerufen]: "Hahn in Ruh",
  [JAGDRUHE_EREIGNIS.JagdFreigegeben]: "Jagd freigeben",
};

export function jagdruheAnwenden(z: JagdruheZustand, e: GespeichertesEreignis | Ereignis): JagdruheZustand {
  const d = e.daten;
  switch (e.typ) {
    case JAGDRUHE_EREIGNIS.JagdzeitplanFestgelegt:
    case JAGDRUHE_EREIGNIS.JagdzeitplanGeaendert:
      return { ...z, festgelegt: true, plan: abschnitteAus(d["Jagdabschnitte"]) };
    case JAGDRUHE_EREIGNIS.HahnInRuhAusgerufen:
    case JAGDRUHE_EREIGNIS.JagdFreigegeben:
      return {
        ...z,
        eingriff: { art: ART[e.typ]!, zeitpunkt: datum(d["Zeitpunkt"]), person: String(d["Person"]), grund: (d["Grund"] as string | null) ?? null },
      };
    case JAGDRUHE_EREIGNIS.ZumZeitplanZurueckgekehrt:
      return { ...z, eingriff: null };
    case JAGDRUHE_EREIGNIS.JagdzustandGewechselt:
      return { ...z, gemeldet: d["Jagdzustand"] as Jagdzustand };
    default:
      return z;
  }
}

export function jagdzustandAus(z: JagdruheZustand, t: Date): Jagdzustand {
  return jagdzustandZu(t, z.plan, z.eingriff);
}

export function naechsterWechselAus(z: JagdruheZustand, t: Date) {
  return naechsterWechsel(t, z.plan, z.eingriff);
}

/** Nächste Grenze eines Jagdabschnitts nach `t` (Zeitgeber, ADR-0017 „scheduled changes“). */
export function naechsteGrenze(z: JagdruheZustand, t: Date): Date | null {
  const punkte = z.plan.flatMap((a) => [a.Beginn, a.Ende]).filter((p) => p.getTime() > t.getTime()).sort((a, b) => a.getTime() - b.getTime());
  return punkte[0] ?? null;
}

/** Ende des letzten Jagdabschnitts (P-29). */
export function letztesEnde(plan: readonly Jagdabschnitt[]): Date | null {
  return plan.reduce<Date | null>((m, a) => (!m || a.Ende.getTime() > m.getTime() ? a.Ende : m), null);
}

// -- Angaben -------------------------------------------------------------------------------------------------------

/** Jagdabschnitte prüfen (F-060): mindestens einer, Beginn vor Ende, am Jagddatum (Europe/Berlin), überschneidungsfrei. */
export function jagdabschnittePruefen(roh: unknown, jagddatum: string): Jagdabschnitt[] {
  if (!Array.isArray(roh) || roh.length === 0) abweisen("kein-jagdabschnitt");
  const plan = roh.map((a) => {
    const o = (a ?? {}) as { Beginn?: unknown; Ende?: unknown };
    const beginn = typeof o.Beginn === "string" ? new Date(o.Beginn) : null;
    const ende = typeof o.Ende === "string" ? new Date(o.Ende) : null;
    if (!beginn || !ende || Number.isNaN(beginn.getTime()) || Number.isNaN(ende.getTime())) abweisen("unzulaessige-angaben");
    return { Beginn: beginn, Ende: ende };
  });
  for (const a of plan) {
    if (a.Beginn.getTime() >= a.Ende.getTime()) abweisen("beginn-nicht-vor-ende");
    if (berlinDatum(a.Beginn) !== jagddatum || berlinDatum(a.Ende) !== jagddatum) abweisen("abschnitt-nicht-am-jagddatum");
  }
  const sortiert = [...plan].sort((x, y) => x.Beginn.getTime() - y.Beginn.getTime());
  for (let i = 1; i < sortiert.length; i++) {
    if (sortiert[i]!.Beginn.getTime() < sortiert[i - 1]!.Ende.getTime()) abweisen("abschnitte-ueberschneiden-sich");
  }
  return sortiert;
}

function grundAus(w: unknown): string | null {
  if (w === undefined || w === null) return null;
  if (typeof w !== "string") abweisen("unzulaessige-angaben");
  const t = w.trim();
  if (t.length > 500) abweisen("unzulaessige-angaben");
  return t || null;
}

function abschnitteDaten(plan: readonly Jagdabschnitt[]) {
  return plan.map((a) => ({ Beginn: a.Beginn, Ende: a.Ende }));
}

/** Status der Drückjagd, soweit die Jagdruhe ihn braucht (Domänenmodell §5, Invarianten der Jagdruhe). */
export type Drueckjagdstatus = "in Vorbereitung" | "bekanntgegeben" | "abgeschlossen" | "abgesagt";

export interface Jagdruhebefehl {
  zustand: JagdruheZustand;
  jagddatum: string;
  status: Drueckjagdstatus;
  /** TeilnehmerId der handelnden Person */
  person: string;
  jetzt: Date;
}

/** Hängt `JagdzustandGewechselt` an, wenn der Zustand nach den Ereignissen zu `jetzt` ein anderer ist (F-088, F-091). */
function mitWechsel(b: Jagdruhebefehl, ereignisse: Ereignis[], ausloeser: Ausloeser): Ereignis[] {
  const nachher = ereignisse.reduce(jagdruheAnwenden, b.zustand);
  const zustand = jagdzustandAus(nachher, b.jetzt);
  if (zustand === nachher.gemeldet) return ereignisse;
  return [...ereignisse, ereignis(JAGDRUHE_EREIGNIS.JagdzustandGewechselt, { Jagdzustand: zustand, Zeitpunkt: b.jetzt, Auslöser: ausloeser, Person: b.person })];
}

function nichtAbgeschlossen(b: Jagdruhebefehl): void {
  if (b.status === "abgeschlossen" || b.status === "abgesagt") abweisen("drueckjagd-abgeschlossen");
}

export function jagdzeitplanFestlegen(b: Jagdruhebefehl, angaben: { Jagdabschnitte?: unknown }): Ereignis[] {
  nichtAbgeschlossen(b);
  const plan = jagdabschnittePruefen(angaben.Jagdabschnitte, b.jagddatum);
  return mitWechsel(b, [ereignis(JAGDRUHE_EREIGNIS.JagdzeitplanFestgelegt, { Jagdabschnitte: abschnitteDaten(plan), Person: b.person, Zeitpunkt: b.jetzt })],
    "Jagdzeitplan ändern");
}

export function jagdzeitplanAendern(b: Jagdruhebefehl, angaben: { Jagdabschnitte?: unknown; Grund?: unknown }): Ereignis[] {
  nichtAbgeschlossen(b);
  const plan = jagdabschnittePruefen(angaben.Jagdabschnitte, b.jagddatum);
  const grund = grundAus(angaben.Grund);
  return mitWechsel(b, [ereignis(JAGDRUHE_EREIGNIS.JagdzeitplanGeaendert, {
    Jagdabschnitte: abschnitteDaten(plan), Person: b.person, Zeitpunkt: b.jetzt, Grund: grund,
  })], "Jagdzeitplan ändern");
}

export function hahnInRuhAusrufen(b: Jagdruhebefehl, angaben: { Grund?: unknown; bestätigt?: unknown }): Ereignis[] {
  nichtAbgeschlossen(b);
  verlangen(angaben.bestätigt === true, "bestaetigung-erforderlich");
  const grund = grundAus(angaben.Grund);
  return mitWechsel(b, [ereignis(JAGDRUHE_EREIGNIS.HahnInRuhAusgerufen, { Person: b.person, Zeitpunkt: b.jetzt, Grund: grund })], "Hahn in Ruh ausrufen");
}

export function jagdFreigeben(b: Jagdruhebefehl, angaben: { Grund?: unknown; bestätigt?: unknown }): Ereignis[] {
  nichtAbgeschlossen(b);
  if (b.status !== "bekanntgegeben") abweisen("drueckjagd-nicht-bekanntgegeben");
  verlangen(angaben.bestätigt === true, "bestaetigung-erforderlich");
  const grund = grundAus(angaben.Grund);
  return mitWechsel(b, [ereignis(JAGDRUHE_EREIGNIS.JagdFreigegeben, { Person: b.person, Zeitpunkt: b.jetzt, Grund: grund })], "Jagd freigeben");
}

/**
 * Zum Zeitplan zurückkehren (F-087): hebt den Eingriff auf. Nach einem Hahn in Ruh, während der Zeitplan gerade Jagd
 * vorsieht, würde das die Jagd grün machen; dann nur mit ausdrücklicher Bestätigung (`bestaetigung-erforderlich`).
 */
export function zumZeitplanZurueckkehren(b: Jagdruhebefehl, angaben: { Grund?: unknown; "ausdrücklich bestätigt"?: unknown }): Ereignis[] {
  nichtAbgeschlossen(b);
  const grund = grundAus(angaben.Grund);
  const e = b.zustand.eingriff;
  if (e?.art === "Hahn in Ruh" && jagdzustandZu(b.jetzt, b.zustand.plan, null) === "Jagd im Gange" && angaben["ausdrücklich bestätigt"] !== true) {
    abweisen("bestaetigung-erforderlich");
  }
  return mitWechsel(b, [ereignis(JAGDRUHE_EREIGNIS.ZumZeitplanZurueckgekehrt, { Person: b.person, Zeitpunkt: b.jetzt, Grund: grund })],
    "Zum Zeitplan zurückkehren");
}

/** Zeitgeber an einer Grenze eines Jagdabschnitts: Wechsel nach Zeitplan mit dem fälligen Zeitpunkt (ADR-0030). */
export function zeitpunktErreicht(z: JagdruheZustand, faellig: Date): Ereignis[] {
  const zustand = jagdzustandAus(z, faellig);
  if (zustand === z.gemeldet) return [];
  return [ereignis(JAGDRUHE_EREIGNIS.JagdzustandGewechselt, { Jagdzustand: zustand, Zeitpunkt: faellig, Auslöser: "Jagdzeitplan", Person: null })];
}
